import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { loadLogo, logoCache, paintStyledQr } from './qrRender';

interface ReferralQrProps {
  /** The URL to encode. */
  value: string;
  /** Logical (CSS) pixel size of the square QR. */
  size?: number;
  /** Public path to the logo placed in the centre. */
  logoSrc?: string;
}

/**
 * Renders a Pyramida-styled QR code: black background, gold modules with a soft
 * sand texture, and the PRMD logo in the centre. The whole image is drawn in a
 * single pass once the logo is ready, so it reads as one cohesive picture rather
 * than an image layered on top. Drawing/encoding logic lives in `qrRender.ts`,
 * which also exports `renderQrDataUrl` for print-resolution downloads.
 */
export const ReferralQr = forwardRef<HTMLCanvasElement, ReferralQrProps>(function ReferralQr(
  { value, size = 220, logoSrc = '/prmd-logo.webp' },
  ref,
) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useImperativeHandle(ref, () => canvasRef.current as HTMLCanvasElement, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;
    let cancelled = false;

    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const px = size * dpr;
    // Display size is driven by width only; CSS keeps it square (aspect-ratio
    // 1/1 + height:auto) so a narrow column never stretches it.
    canvas.style.width = '100%';
    canvas.style.maxWidth = `${size}px`;
    canvas.style.height = 'auto';

    // Paint immediately (with the logo if already decoded, else just the pad),
    // then repaint once the logo finishes decoding so there's no overlay pop-in.
    const cached = logoCache.get(logoSrc);
    paintStyledQr(canvas, value, px, cached ?? undefined);
    if (!cached) {
      void loadLogo(logoSrc).then((img) => {
        if (cancelled || canvasRef.current !== canvas) return;
        paintStyledQr(canvas, value, px, img ?? undefined);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [value, size, logoSrc]);

  return (
    <canvas
      ref={canvasRef}
      className="referral-qr"
      role="img"
      aria-label={t('refLink.qrAria')}
    />
  );
});
